import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setLanguage } from '../../../features/langSlice';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import PractitionerBadge from '../../Customer/components/PractitionerBadge';
import BoomTestimonials from '../../Customer/components/BoomTestimonials';
import useReviewStats from '../../../hooks/useReviewStats';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImage from '../../../assets/hero-lp-pills.webp';
import RiisePouch from '../../../assets/yellow-paper-pouch-granules.webp';
import LisaPreview from '../../../assets/8-preview.webp';
import FatigueImage from '../../../assets/wechsel2/fatigue.webp';
import BrainFogImage from '../../../assets/wechsel2/brain-fog.webp';
import SleepImage from '../../../assets/wechsel2/sleep.webp';
import MoodImage from '../../../assets/wechsel2/mood.webp';
import JointsImage from '../../../assets/wechsel2/joints.webp';
import MetabolismImage from '../../../assets/wechsel2/metabolism.webp';
import ReframeImage from '../../../assets/wechsel2/reframe.webp';

// v1 (first pass): same copy as /pages/wechsel, restructured as a listicle
// (build reference: papasshorts.de "die-leichte-chino..." — eyebrow, stat row,
// comparison table, then numbered points with CTA banners in between).
// Every string below is an EXISTING i18n key already used on /pages/wechsel
// itself (LP_Wechsel_*, plus the same shared Review/BoomTestimonial/MakeChoice
// content that page already renders) — no copy from any other page, and no
// new marketing copy was written. Only the layout changed. See CLAUDE.md:
// never hardcode copy.
const K = {
  HeroH1Part1:      'LP_Wechsel_HeroH1Part1',
  HeroH1Part2:      'LP_Wechsel_HeroH1Part2',
  HeroCta:          'LP_Wechsel_HeroCta',
  CtaSub:           'LP_CtaSub',
  FatigueImageAlt:    'LP_Wechsel2_FatigueImageAlt',
  BrainFogImageAlt:   'LP_Wechsel2_BrainFogImageAlt',
  SleepImageAlt:      'LP_Wechsel2_SleepImageAlt',
  MoodImageAlt:       'LP_Wechsel2_MoodImageAlt',
  JointsImageAlt:     'LP_Wechsel2_JointsImageAlt',
  MetabolismImageAlt: 'LP_Wechsel2_MetabolismImageAlt',
  ReframeImageAlt:    'LP_Wechsel2_ReframeImageAlt',
  PouchImageAlt:    'LP_Wechsel_PouchImageAlt',
  StickyLine1:      'LP_Wechsel_StickyLine1',
  StickyLine2:      'LP_Wechsel_StickyLine2',
  ByLine:           'LP_Listicle_ByLine',

  Stat1Val: 'LP_Pills_Stat1Val', Stat1Label: 'LP_Pills_Stat1Label',
  Stat2Val: 'LP_Pills_Stat2Val', Stat2Label: 'LP_Pills_Stat2Label',
  // Quiz duration instead of wechsel-2's outcome-percentage stat: this variant
  // makes no outcome claim (first-time supplement skeptics traffic).
  Stat3Val: 'LP_Wechsel3_Stat3Val', Stat3Label: 'LP_Wechsel3_Stat3Label',
  Stat4Val: 'LP_Pills_Stat4Val', Stat4Label: 'LP_Pills_Stat4Label',

  HeroSublineFull:      'LP_Wechsel3_HeroSublineFull',
  ListicleLeadIn:       'LP_Wechsel2_ListicleLeadIn',
  Vignette1:            'LP_Wechsel_Vignette1',
  Vignette2:            'LP_Wechsel_Vignette2',
  Vignette3:            'LP_Wechsel_Vignette3',

  // Comparison table rows: personalization/adaptability decision-criteria
  // (not the symptoms themselves, which the listicle below already covers).
  Compare1F: 'LP_Wechsel2Compare_Feature1', Compare1R: 'LP_Wechsel2Compare_Riise1', Compare1O: 'LP_Wechsel2Compare_Other1',
  Compare2F: 'LP_Wechsel2Compare_Feature2', Compare2R: 'LP_Wechsel2Compare_Riise2', Compare2O: 'LP_Wechsel2Compare_Other2',
  Compare3F: 'LP_Wechsel2Compare_Feature3', Compare3R: 'LP_Wechsel2Compare_Riise3', Compare3O: 'LP_Wechsel2Compare_Other3',
  Compare4F: 'LP_Wechsel2Compare_Feature4', Compare4R: 'LP_Wechsel2Compare_Riise4', Compare4O: 'LP_Wechsel2Compare_Other4',
  Compare5F: 'LP_Wechsel2Compare_Feature5', Compare5R: 'LP_Wechsel2Compare_Riise5', Compare5O: 'LP_Wechsel2Compare_Other5',
  CompareOtherHeader: 'LP_Wechsel3Compare_OtherHeader',

  ProductBullet1: 'LP_Wechsel2_ProductBullet1',
  ProductBullet2: 'LP_Wechsel2_ProductBullet2',
  ProductBullet3: 'LP_Wechsel2_ProductBullet3',
  ProductBullet4: 'LP_Wechsel2_ProductBullet4',

  // Body copy for points 1-6, same underlying fact as /pages/wechsel's
  // Vignette*Detail/Focus*Phys keys, with one added clause contrasting a
  // generic/fixed-dose supplement — comparison stays in this general
  // "personalization" territory, not a treatment claim (that stays isolated
  // in the "in your formula" block below).
  Body1: 'LP_Wechsel2_Body1',
  Body2: 'LP_Wechsel2_Body2',
  Body3: 'LP_Wechsel2_Body3',
  Body4: 'LP_Wechsel2_Body4',
  Body5: 'LP_Wechsel2_Body5',
  Body6: 'LP_Wechsel2_Body6',

  // Attribution under each in-list customer quote. Was hardcoded English
  // ("verified riise customer") on a page that defaults to German — now a key,
  // worded to match the existing LP_*_FeatAuthor attributions site-wide.
  ReviewAttribution: 'LP_Wechsel2_ReviewAttribution',

  RatingCat1: 'LP_Wechsel2_RatingCat1',
  RatingCat2: 'LP_Wechsel2_RatingCat2',
  RatingCat3: 'LP_Wechsel2_RatingCat3',
  RatingCat4: 'LP_Wechsel2_RatingCat4',
  FeatQuote:  'LP_Guess_FeatQuote',
  FeatAuthor: 'LP_Guess_FeatAuthor',

  ProductBoxHeading: 'LP_Wechsel2_ProductBoxHeading',
  ProductBoxPrice:   'LP_Wechsel2_ProductBoxPrice',
  SolutionLabel:     'LP_WechselListicle_SolutionLabel',

  GermanyReviewsHeading: 'LP_Wechsel2_GermanyReviewsHeading',
  GermanyReview1: 'LP_Wechsel2_GermanyReview1', GermanyReview1Name: 'LP_Wechsel2_GermanyReview1Name',
  GermanyReview2: 'LP_Wechsel2_GermanyReview2', GermanyReview2Name: 'LP_Wechsel2_GermanyReview2Name',
  GermanyReview3: 'LP_Wechsel2_GermanyReview3', GermanyReview3Name: 'LP_Wechsel2_GermanyReview3Name',
  GermanyReview4: 'LP_Wechsel2_GermanyReview4', GermanyReview4Name: 'LP_Wechsel2_GermanyReview4Name',
  GermanyReviewsNote: 'LP_Wechsel2_GermanyReviewsNote',

  FaqHeading: 'LP_Wechsel2_FaqHeading',
  Faq1Q: 'LP_Wechsel2_Faq1Q', Faq1A: 'LP_Wechsel2_Faq1A',
  Faq2Q: 'LP_Wechsel2_Faq2Q', Faq2A: 'LP_Wechsel2_Faq2A',
  Faq3Q: 'LP_Wechsel2_Faq3Q', Faq3A: 'LP_Wechsel2_Faq3A',
  Faq4Q: 'LP_Wechsel2_Faq4Q', Faq4A: 'LP_Wechsel2_Faq4A',
  Faq5Q: 'LP_Wechsel2_Faq5Q', Faq5A: 'LP_Wechsel2_Faq5A',

  FocusClaimLabel:   'LP_Wechsel_FocusClaimLabel',
  Focus2Title: 'LP_Wechsel_Focus2Title', Focus2Desc: 'LP_Wechsel_Focus2Desc',
  Focus3Desc: 'LP_Wechsel_Focus3Desc',
  Focus4Desc: 'LP_Wechsel_Focus4Desc',
  Focus5Desc: 'LP_Wechsel_Focus5Desc',
  // Narrative "moment" titles for points 4-6, matching the compliant vignette
  // pattern used by points 1-3 (before-vs-now scene, no condition named, no
  // health claim in the title itself) — Meta ad policy needs this indirection;
  // the actual EFSA-authorized claim stays in the separate "in your formula" block.
  Point4Title: 'LP_Wechsel2_Point4Title',
  Point5Title: 'LP_Wechsel2_Point5Title',
  Point6Title: 'LP_Wechsel3_Point6Title',
  // Point 7 close: no "disease" wording, no "this isn't X, it's Y" structure.
  Point7Title: 'LP_Wechsel2_Point7Title',
  Focus6Title: 'LP_Wechsel_Focus6Title', Focus6Desc: 'LP_Wechsel_Focus6Desc',
  Focus7Desc:  'LP_Wechsel_Focus7Desc',

  ShiftP1:           'LP_Wechsel_ShiftP1',
  ShiftP2:           'LP_Wechsel_ShiftP2',

  HowWorksHeading: 'LP_Wechsel_HowWorksHeading',
  Step1Title: 'LP_Wechsel_Step1Title', Step1Desc: 'LP_Wechsel_Step1Desc',
  Step2Title: 'LP_Wechsel_Step2Title', Step2Desc: 'LP_Wechsel3_Step2Desc',
  Step3Title: 'LP_Wechsel_Step3Title', Step3Desc: 'LP_Wechsel_Step3Desc',

  ExpertHeading:  'LP_Wechsel_ExpertHeading',
  FinalHeading:   'LP_Wechsel_FinalHeading',
  FinalSubline:   'LP_Wechsel_FinalSubline',
  FinalCta:       'LP_Wechsel_FinalCta',
  AdDisclosure:   'LP_Pills_AdDisclosure',
};

// Bump this if listicle points are added/removed — it drives the "Here are
// {{count}} reasons..." line in the hero, so the two stay in sync.
const LISTICLE_POINT_COUNT = 7;

// Intrinsic size of every src/assets/wechsel2 image. Passed to <img> as
// width/height so the browser reserves the right box before a lazy-loaded
// image arrives — without these, all 7 portrait images shift the page as
// they land. Keep in sync if the assets are ever re-exported.
const POINT_IMAGE_W = 896;
const POINT_IMAGE_H = 1200;

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true }) => {
  const { metaEvent } = useMeta();
  return (
    <CustomLink
      to="/quiz"
      onClick={() => metaEvent('Lead')}
      className="lp-btn-cta"
      style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, background: '#F6E147', color: '#103642',
        fontWeight: 700, fontSize: 19, borderRadius: 999,
        padding: '18px 36px', textDecoration: 'none',
        width: full ? '100%' : 'auto' }}
    >
      {children}
      <span aria-hidden="true">→</span>
    </CustomLink>
  );
};

const Section = ({ bg = '#fff', py = 56, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const Chevron = ({ open, color = '#103642' }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
    style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
    <path d="M2.5 5l4.5 4.5L11.5 5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StarRow = ({ count = 5, size = 18 }) => (
  <div style={{ display: 'flex', gap: 3 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

const NumberBadge = ({ n }) => (
  <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SERIF,
    fontWeight: 400, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

// Real, already-vetted customer quotes (same source keys used on /pages/wechsel
// and /pages/wechsel-listicle) — one per relevant point, never fabricated.
const ReviewQuote = ({ quote, name, isDesktop }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingLeft: isDesktop ? 4 : 0 }}>
      <svg width="20" height="16" viewBox="0 0 20 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M0 16V9.6C0 4.3 3.2.9 8 0l1 2.4C6.1 3.4 4.4 5.5 4.2 8H8v8H0zm11 0V9.6c0-5.3 3.2-8.7 8-9.6l1 2.4c-2.9 1-4.6 3.1-4.8 5.6H19v8h-8z" fill="#F6E147" />
      </svg>
      <Stack gap={4}>
        <p style={{ ...SANS, fontSize: 14.5, color: '#103642', margin: 0, lineHeight: 1.55, fontStyle: 'italic' }}>
          {quote}
        </p>
        <p style={{ ...SANS, fontSize: 12.5, color: 'rgba(16,54,66,0.5)', margin: 0, fontWeight: 600 }}>
          — {t(K.ReviewAttribution, { name })}
        </p>
      </Stack>
    </div>
  );
};

// One numbered reason: moment + general mechanism explainer, with the
// EFSA-authorized claim (if any) kept in its own visually separated block —
// same compliance posture as /pages/wechsel and /pages/wechsel-listicle.
// `image` is the real ingredient photo backing that point's formula claim
// (from src/assets/focusareas) — per-point visuals instead of one header hero.
const ListiclePoint = ({ n, title, body, image, imageAlt, claimLabel, claim, review, isDesktop }) => (
  <div style={{ display: 'flex', gap: isDesktop ? 24 : 16 }}>
    <NumberBadge n={n} />
    <Stack gap={12} style={{ flex: 1 }}>
      <p style={{ ...SANS, fontSize: isDesktop ? 19 : 17, fontWeight: 700,
        color: '#103642', margin: 0, lineHeight: 1.4 }}>
        {title}
      </p>
      {image && (
        <img src={image} alt={imageAlt || ''}
          width={POINT_IMAGE_W} height={POINT_IMAGE_H}
          style={{ width: '100%', maxWidth: isDesktop ? 320 : '100%',
            height: 'auto', aspectRatio: `${POINT_IMAGE_W} / ${POINT_IMAGE_H}`,
            borderRadius: 14, display: 'block' }} loading="lazy" />
      )}
      <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)', margin: 0, lineHeight: 1.7 }}>
        {body}
      </p>
      {claim && (
        <div style={{ background: 'rgba(17,109,110,0.06)', border: '1px solid rgba(17,109,110,0.14)',
          borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
            <circle cx="10" cy="10" r="9" stroke="#116D6E" strokeWidth="1.4" />
            <path d="M6 10.2l2.6 2.6L14 7.5" stroke="#116D6E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <p style={{ ...SANS, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#116D6E', margin: '0 0 4px 0' }}>
              {claimLabel}
            </p>
            <p style={{ ...SANS, fontSize: 13.5, color: '#103642', margin: 0, lineHeight: 1.55 }}>
              {claim}
            </p>
          </div>
        </div>
      )}
      {review && <ReviewQuote quote={review.quote} name={review.name} isDesktop={isDesktop} />}
    </Stack>
  </div>
);

const CtaBanner = ({ heading, cta, isDesktop }) => (
  <Section bg="#103642" maxW={isDesktop ? 1200 : 480} px={isDesktop ? 80 : 24} py={40}>
    <Stack gap={16} align="center" style={{ textAlign: 'center' }}>
      <p style={{ ...SERIF, fontSize: isDesktop ? 26 : 21, lineHeight: 1.25, fontWeight: 400,
        color: '#fff', margin: 0, maxWidth: 480 }}>
        {heading}
      </p>
      <div style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
        <CtaButton full={!isDesktop}>{cta}</CtaButton>
      </div>
    </Stack>
  </Section>
);

// Papasshorts-style trust-stat row, directly under the hero. Reuses the same
// stat keys already shipped elsewhere on the site (/pages/pills-or-sachet) —
// not new numbers.
const StatRow = ({ t, isDesktop, reviewStats }) => {
  const stat4Params = {
    rating: reviewStats?.average || '4.88',
    count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
  };
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
      gap: isDesktop ? 24 : 20, textAlign: 'center',
    }}>
      {[
        { val: K.Stat1Val, label: K.Stat1Label },
        { val: K.Stat2Val, label: K.Stat2Label },
        { val: K.Stat3Val, label: K.Stat3Label },
        { val: K.Stat4Val, label: K.Stat4Label, params: stat4Params },
      ].map(({ val, label, params }, i) => (
        <Stack key={i} gap={4} align="center" style={{ minWidth: 0 }}>
          <p style={{ ...SERIF, fontSize: isDesktop ? 26 : 22, lineHeight: 1, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(val, params)}
          </p>
          <p style={{ ...SANS, fontSize: isDesktop ? 10.5 : 10.5, color: 'rgba(16,54,66,0.55)', margin: 0,
            lineHeight: 1.3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em',
            overflowWrap: 'break-word' }}>
            {t(label, params)}
          </p>
        </Stack>
      ))}
    </div>
  );
};

// Papasshorts-style "VERGLEICH" table: a real value in each column (not a
// checkmark/cross grid like the site's existing MakeChoice component — this
// is a separate, purpose-built element). Rows are the themes that actually
// recur across this page's listicle points and "The Shift" section:
// personalization, microgranule format, ingredient count, the formula
// adapting to changing needs (vs. a fixed 45+ multivitamin), and routine.
const CompareTable = ({ t, K, isDesktop }) => {
  const rows = [
    { f: K.Compare1F, r: K.Compare1R, o: K.Compare1O },
    { f: K.Compare2F, r: K.Compare2R, o: K.Compare2O },
    { f: K.Compare3F, r: K.Compare3R, o: K.Compare3O },
    { f: K.Compare4F, r: K.Compare4R, o: K.Compare4O },
    { f: K.Compare5F, r: K.Compare5R, o: K.Compare5O },
  ];
  return (
    <Section bg="#f5f7f7" maxW={isDesktop ? 640 : 480} px={isDesktop ? 80 : 24} py={isDesktop ? 32 : 24}>
      <div style={{ borderRadius: 16, border: '1px solid rgba(16,54,66,0.1)',
        overflow: 'hidden', background: '#fff' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr' }}>
          <div style={{ padding: isDesktop ? '14px 16px' : '10px 10px' }} />
          <div style={{ padding: isDesktop ? '14px 16px' : '10px 8px', textAlign: 'center',
            background: 'rgba(17,109,110,0.08)', borderBottom: '1px solid rgba(16,54,66,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <img src={logo_home} alt="riise" style={{ height: 18, width: 'auto' }} />
            <img src={RiisePouch} alt="" style={{ height: 18, width: 9, objectFit: 'contain' }} />
          </div>
          <div style={{ padding: isDesktop ? '14px 16px' : '10px 8px', textAlign: 'center',
            borderBottom: '1px solid rgba(16,54,66,0.1)', borderLeft: '1px solid rgba(16,54,66,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ ...SANS, fontSize: isDesktop ? 12 : 10.5, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(16,54,66,0.55)' }}>
              {t(K.CompareOtherHeader)}
            </span>
          </div>
        </div>
        {rows.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr',
            borderTop: '1px solid rgba(16,54,66,0.08)' }}>
            <div style={{ padding: isDesktop ? '16px' : '12px 10px', display: 'flex', alignItems: 'center' }}>
              <span style={{ ...SANS, fontSize: isDesktop ? 13.5 : 12.5, fontWeight: 600, color: '#103642' }}>
                {t(row.f)}
              </span>
            </div>
            <div style={{ padding: isDesktop ? '16px 12px' : '12px 8px', textAlign: 'center',
              background: 'rgba(17,109,110,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: isDesktop ? 13.5 : 12, fontWeight: 600, color: '#116D6E', lineHeight: 1.3 }}>
                {t(row.r)}
              </span>
            </div>
            <div style={{ padding: isDesktop ? '16px 12px' : '12px 8px', textAlign: 'center',
              borderLeft: '1px solid rgba(16,54,66,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: isDesktop ? 13.5 : 12, color: 'rgba(16,54,66,0.5)', lineHeight: 1.3 }}>
                {t(row.o)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

// Papasshorts-style "4 category ratings + one testimonial" block. The 4
// category ratings all show the same real, rounded average (reviewStats),
// since riise's reviews.io page doesn't publish separate per-category scores
// — showing distinct fabricated numbers per category would overclaim, so
// each label just applies the one true aggregate rating to a theme that
// genuinely recurs in riise's real reviews (personalization, ease of use,
// tolerability, support). The quote is Lisa's real, already-published
// testimonial (same LP_Guess_FeatQuote/Author used on /pages/stop-guessing).
const RatingBenefits = ({ t, K, isDesktop, reviewStats }) => {
  const starCount = Math.round(Number(reviewStats?.average) || 4.8);
  const cats = [K.RatingCat1, K.RatingCat2, K.RatingCat3, K.RatingCat4];
  return (
    <Section bg="#fff" maxW={isDesktop ? 640 : 480} px={isDesktop ? 80 : 24} py={isDesktop ? 56 : 40}>
      <Stack gap={40}>
        <Stack gap={20} align="center">
          {cats.map((cat) => (
            <Stack key={cat} gap={8} align="center" style={{ textAlign: 'center' }}>
              <span style={{ ...SANS, fontSize: isDesktop ? 11 : 9.5, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(16,54,66,0.5)' }}>
                {t(cat)}
              </span>
              <StarRow count={starCount} size={isDesktop ? 16 : 12} />
            </Stack>
          ))}
        </Stack>
        <Stack gap={20}>
          <StarRow count={5} />
          <p style={{ ...SANS, fontSize: isDesktop ? 22 : 18, lineHeight: 1.6, fontWeight: 400,
            color: '#103642', margin: 0, fontStyle: 'italic' }}>
            "{t(K.FeatQuote)}"
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={LisaPreview} alt="Lisa" style={{ width: 48, height: 48, borderRadius: '50%',
              objectFit: 'cover', flexShrink: 0 }} />
            <p style={{ ...SANS, fontSize: 14, fontWeight: 600, color: 'rgba(16,54,66,0.6)', margin: 0 }}>
              {t(K.FeatAuthor)}
            </p>
          </div>
        </Stack>
      </Stack>
    </Section>
  );
};

// Papasshorts-style final product box: product photo, heading, the same
// "what matters" bullets as the comparison table above, "from 1€/day"
// instead of a price (real, already-used pricing line — MillionCombinations3
// elsewhere in this codebase), and one big CTA.
const ProductBox = ({ t, K, isDesktop }) => {
  const bullets = [K.ProductBullet1, K.ProductBullet2, K.ProductBullet3, K.ProductBullet4];
  return (
    <Section bg="#f5f7f7" maxW={isDesktop ? 640 : 480} px={isDesktop ? 80 : 24} py={isDesktop ? 56 : 40}>
      <div style={{ borderRadius: 20, background: '#fff', border: '2px solid #103642',
        overflow: 'hidden' }}>
        <img src={HeroImage} alt={t(K.PouchImageAlt)}
          style={{ width: '100%', height: 'auto', display: 'block' }} />
        <div style={{ padding: isDesktop ? 40 : 24 }}>
          <Stack gap={20}>
            <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: '#116D6E' }}>
              {t(K.SolutionLabel)}
            </span>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 32 : 26, lineHeight: 1.2, fontWeight: 400,
              color: '#103642', margin: 0 }}>
              {t(K.ProductBoxHeading)}
            </h2>
            <Stack gap={10}>
              {bullets.map((b) => (
                <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="10" cy="10" r="9" stroke="#116D6E" strokeWidth="1.4" />
                    <path d="M6 10.2l2.6 2.6L14 7.5" stroke="#116D6E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ ...SANS, fontSize: 14.5, color: '#103642' }}>{t(b)}</span>
                </div>
              ))}
            </Stack>
            <p style={{ ...SERIF, fontSize: isDesktop ? 28 : 24, fontWeight: 400, color: '#103642', margin: 0 }}>
              {t(K.ProductBoxPrice)}
            </p>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
            <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)', margin: 0, textAlign: 'center' }}>
              {t(K.CtaSub)}
            </p>
          </Stack>
        </div>
      </div>
    </Section>
  );
};

// "This is what women from all over Germany are saying" — 4 real,
// already-published customer reviews from riise's own reviews.io storefront
// (reviews.io/company-reviews/store/weriise.co), not fabricated quotes.
const GermanyReviews = ({ t, K, isDesktop }) => {
  const reviews = [
    { q: K.GermanyReview1, n: K.GermanyReview1Name },
    { q: K.GermanyReview2, n: K.GermanyReview2Name },
    { q: K.GermanyReview3, n: K.GermanyReview3Name },
    { q: K.GermanyReview4, n: K.GermanyReview4Name },
  ];
  return (
    <Section bg="#fff" maxW={isDesktop ? 1200 : 480} px={isDesktop ? 80 : 24} py={isDesktop ? 64 : 40}>
      <Stack gap={32}>
        <h2 style={{ ...SERIF, fontSize: isDesktop ? 34 : 26, lineHeight: 1.2, fontWeight: 400,
          color: '#103642', margin: 0, textAlign: 'center' }}>
          {t(K.GermanyReviewsHeading)}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : '1fr',
          gap: 16 }}>
          {reviews.map((rv) => (
            <div key={rv.q} className="lp-card" style={{ background: '#f5f7f7', borderRadius: 16,
              padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <StarRow count={5} size={14} />
              <p style={{ ...SANS, fontSize: 14.5, color: '#103642', margin: 0, lineHeight: 1.6, flex: 1 }}>
                "{t(rv.q)}"
              </p>
              <p style={{ ...SANS, fontSize: 13, fontWeight: 600, color: 'rgba(16,54,66,0.5)', margin: 0 }}>
                — {t(rv.n)}
              </p>
            </div>
          ))}
        </div>
        <p style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', margin: 0, textAlign: 'center' }}>
          {t(K.GermanyReviewsNote)}
        </p>
      </Stack>
    </Section>
  );
};

// A single FAQ row, own lightweight accordion (matches this page's visual
// language instead of importing the generic Tailwind-styled Accordion used
// elsewhere in the codebase).
const FaqRow = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(16,54,66,0.1)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ ...SANS, width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: '18px 0', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 16 }}
      >
        <span style={{ fontSize: 15.5, fontWeight: 700, color: '#103642', lineHeight: 1.4 }}>{q}</span>
        <Chevron open={open} color="#103642" />
      </button>
      {open && (
        <p style={{ ...SANS, fontSize: 14.5, color: 'rgba(16,54,66,0.65)', lineHeight: 1.65,
          margin: '0 0 20px 0' }}>
          {a}
        </p>
      )}
    </div>
  );
};

// "Common questions" — addresses the medications/conditions claim from the
// comparison table above (otherwise asserted and never backed up), plus the
// "formula adjusts with you" claim repeated across the page.
const FaqSection = ({ t, K, isDesktop }) => {
  const rows = [
    { q: K.Faq1Q, a: K.Faq1A },
    { q: K.Faq2Q, a: K.Faq2A },
    { q: K.Faq3Q, a: K.Faq3A },
    { q: K.Faq4Q, a: K.Faq4A },
    { q: K.Faq5Q, a: K.Faq5A },
  ];
  return (
    <Section bg="#fff" maxW={isDesktop ? 640 : 480} px={isDesktop ? 80 : 24} py={isDesktop ? 56 : 40}>
      <Stack gap={24}>
        <h2 style={{ ...SERIF, fontSize: isDesktop ? 32 : 26, lineHeight: 1.15, fontWeight: 400,
          color: '#103642', margin: 0, textAlign: 'center' }}>
          {t(K.FaqHeading)}
        </h2>
        <div style={{ borderTop: '1px solid rgba(16,54,66,0.1)' }}>
          {rows.map((row) => (
            <FaqRow key={row.q} q={t(row.q)} a={t(row.a)} />
          ))}
        </div>
      </Stack>
    </Section>
  );
};

const StepNumber = ({ n }) => (
  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SANS,
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

const LangSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(localStorage.getItem('lang') || 'de');
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchTo = (lng) => {
    localStorage.setItem('lang', lng);
    i18n.changeLanguage(lng);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lang', lng);
    navigate({ search: newParams.toString() });
    setSelected(lng);
    dispatch(setLanguage({ lng }));
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        className="lp-lang-btn"
        style={{ ...SANS, background: 'none', border: '1px solid rgba(16,54,66,0.2)',
          borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#103642',
          display: 'flex', alignItems: 'center', gap: 5 }}
      >
        {selected.toUpperCase()}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="#103642" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          overflow: 'hidden', minWidth: 110, zIndex: 200,
        }}>
          {[
            { code: 'de', label: 'Deutsch' },
            { code: 'en', label: 'English' },
          ].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => switchTo(code)}
              style={{ ...SANS, display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: selected === code ? '#f5f7f7' : '#fff',
                cursor: 'pointer', fontSize: 13, color: '#103642',
                fontWeight: selected === code ? 600 : 400, textAlign: 'left' }}
            >
              <i className={`flag-icon flag-icon-${code === 'en' ? 'us' : 'de'}`} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StickyBar = ({ t, K, isDesktop }) => {
  const { metaEvent } = useMeta();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
      transform: visible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.8)',
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          {t(K.StickyLine1)}<br />
          <span style={{ color: '#F6E147', fontWeight: 600 }}>{t(K.StickyLine2)}</span>
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

const Wechsel3 = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reviewStats = useReviewStats();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Same behavior as /pages/wechsel and /pages/wechsel-listicle: German is the
  // default for this page regardless of the sitewide i18n fallback, unless the
  // URL explicitly says ?lang=en.
  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxW = isDesktop ? 640 : 480;
  const wideMaxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>{t(K.HeroH1Part1)} {t(K.HeroH1Part2)} | riise.</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── Header ── */}
      <header style={{ background: '#fff', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isDesktop ? '18px 80px' : '16px 20px' }}>
        <div style={{ width: 60, flexShrink: 0 }} />
        <img src={logo_home} alt="riise" style={{ height: 24, width: 'auto' }} />
        <LangSwitcher />
      </header>

      {/* ── Hero, papasshorts-style: H1 → byline → trust stats → body.
           No header image — each point below carries its own image instead.
           No CTA here — the sticky bar and CTA banners further down carry that. ── */}
      <Section bg="#F9F9F9" maxW={maxW} px={px} py={isDesktop ? 48 : 32}>
        <Stack gap={20} align="center" style={{ textAlign: 'center' }}>
          <PractitionerBadge />
          <h1 style={{ ...SERIF, fontSize: isDesktop ? 44 : 32, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.HeroH1Part1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
          </h1>
          <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.5)', margin: 0, fontWeight: 600 }}>
            {t(K.ByLine, { min: 4 })}
          </p>
          <div style={{ width: '100%', paddingTop: 4 }}>
            <StatRow t={t} isDesktop={isDesktop} reviewStats={reviewStats} />
          </div>
          <p style={{ ...SANS, fontSize: isDesktop ? 17 : 15, color: 'rgba(16,54,66,0.65)',
            lineHeight: 1.7, margin: 0, maxWidth: 520 }}>
            {t(K.HeroSublineFull, { count: LISTICLE_POINT_COUNT })}
          </p>
        </Stack>
      </Section>

      {/* ── Comparison table, papasshorts "VERGLEICH" style: a real value per
           column (not just a checkmark), rows picked from what actually
           recurs across this page — personalization, format, ingredient
           count, adapting formula, daily routine. ── */}
      <CompareTable t={t} K={K} isDesktop={isDesktop} />

      {/* ── Lead-in box, papasshorts "⚡ Diese 5 Vorteile..." equivalent —
           bridges the table into the numbered list. Uses riise's own brand
           motto ("the simplest supplement routine in the world" /
           SimpleSupplements key elsewhere in this codebase), not a new claim. ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={8}>
        <div style={{ background: 'rgba(17,109,110,0.06)', border: '1px solid rgba(17,109,110,0.14)',
          borderRadius: 14, padding: '16px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center' }}>
          <span aria-hidden="true" style={{ fontSize: 15, flexShrink: 0 }}>⚡</span>
          <p style={{ ...SANS, fontSize: isDesktop ? 15.5 : 14, fontWeight: 700, color: '#103642', margin: 0 }}>
            {t(K.ListicleLeadIn, { count: LISTICLE_POINT_COUNT })}
          </p>
        </div>
      </Section>

      {/* ── Points 1-3 ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={48}>
        <Stack gap={40}>
          <ListiclePoint n={1} isDesktop={isDesktop}
            title={t(K.Vignette1)} body={t(K.Body1)}
            image={FatigueImage} imageAlt={t(K.FatigueImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus7Desc)}
            review={{ quote: t('BoomTestimonial4Review'), name: t('BoomTestimonial4Name') }} />
          <ListiclePoint n={2} isDesktop={isDesktop}
            title={t(K.Vignette2)} body={t(K.Body2)}
            image={BrainFogImage} imageAlt={t(K.BrainFogImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus6Desc)} />
          <ListiclePoint n={3} isDesktop={isDesktop}
            title={t(K.Vignette3)} body={t(K.Body3)}
            image={SleepImage} imageAlt={t(K.SleepImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus2Desc)}
            review={{ quote: t('BoomTestimonial2Review'), name: t('BoomTestimonial2Name') }} />
        </Stack>
      </Section>

      {/* ── CTA #1 — same sticky-bar line from /pages/wechsel, used as a banner here ── */}
      <CtaBanner isDesktop={isDesktop} heading={t(K.StickyLine1)} cta={t(K.HeroCta)} />

      {/* ── Points 4-6 ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={48}>
        <Stack gap={40}>
          <ListiclePoint n={4} isDesktop={isDesktop}
            title={t(K.Point4Title)} body={t(K.Body4)}
            image={MoodImage} imageAlt={t(K.MoodImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus3Desc)} />
          <ListiclePoint n={5} isDesktop={isDesktop}
            title={t(K.Point5Title)} body={t(K.Body5)}
            image={JointsImage} imageAlt={t(K.JointsImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus4Desc)} />
          <ListiclePoint n={6} isDesktop={isDesktop}
            title={t(K.Point6Title)} body={t(K.Body6)}
            image={MetabolismImage} imageAlt={t(K.MetabolismImageAlt)}
            claimLabel={t(K.FocusClaimLabel)} claim={t(K.Focus5Desc)}
            review={{ quote: t('BoomTestimonial9Review'), name: t('BoomTestimonial9Name') }} />
        </Stack>
      </Section>

      {/* ── CTA #2 — same sticky-bar line from /pages/wechsel, used as a banner here ── */}
      <CtaBanner isDesktop={isDesktop} heading={t(K.StickyLine2)} cta={t(K.HeroCta)} />

      {/* ── Point 7 — the close, reframed without "disease" wording or the
           "this isn't X, it's Y" structure. Body still sourced from
           /pages/wechsel's ShiftP1/P2 (the "not a generic 45+ multivitamin"
           point), only the title is new. ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={48}>
        <ListiclePoint n={7} isDesktop={isDesktop}
          title={t(K.Point7Title)}
          image={ReframeImage} imageAlt={t(K.ReframeImageAlt)}
          body={`${t(K.ShiftP1)} ${t(K.ShiftP2)}`} />
      </Section>

      {/* ── Benefits-with-stars block, papasshorts style ── */}
      <RatingBenefits t={t} K={K} isDesktop={isDesktop} reviewStats={reviewStats} />

      {/* ── Product box, papasshorts style: photo, heading, bullets, "from
           1€/day" instead of a price, one big CTA ── */}
      <ProductBox t={t} K={K} isDesktop={isDesktop} />

      {/* ── "This is what women from all over Germany are saying" — real
           reviews.io quotes ── */}
      <GermanyReviews t={t} K={K} isDesktop={isDesktop} />

      {/* ── How it works ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={40}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 32 : 26, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.HowWorksHeading)}
          </h2>
          <Stack gap={32}>
            {[
              { n: 1, title: K.Step1Title, desc: K.Step1Desc },
              { n: 2, title: K.Step2Title, desc: K.Step2Desc },
              { n: 3, title: K.Step3Title, desc: K.Step3Desc },
            ].map(({ n, title, desc }) => (
              <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                <StepNumber n={n} />
                <Stack gap={8}>
                  <p style={{ ...SANS, fontSize: 16, fontWeight: 600, color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)', margin: 0, lineHeight: 1.65 }}>
                    {t(desc)}
                  </p>
                </Stack>
              </div>
            ))}
          </Stack>
        </Stack>
      </Section>

      {/* ── Trust block ── */}
      <Section bg="#f5f7f7" maxW={wideMaxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 32 : 26, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 450 }}>
              <PractitionerBadge />
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Video testimonials (real, existing content) ── */}
      <BoomTestimonials />

      {/* ── FAQ — answers the medications/conditions claim from the
           comparison table above, which otherwise goes unexplained. ── */}
      <FaqSection t={t} K={K} isDesktop={isDesktop} />

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `72px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 38 : 27, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7 }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
              <CtaButton full={!isDesktop}>{t(K.FinalCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      <StickyBar t={t} K={K} isDesktop={isDesktop} />

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise" style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Privacy Policy
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)', textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default Wechsel3;
